import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, SelectPicker, Checkbox, NumberInput } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { getCitiesLookup } from '@/services/city.service';
import { Story, StoryStatusEnum, StoryStatusList, UpsertStory } from '@/models/story.model';
import {upsertStory } from '@/services/story.service';
import Textarea from '@/components/Textarea';
import { getCountriesLookup } from '@/services/country.service';

const storySchema = Schema.Model<UpsertStory>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  bannerUrl: Schema.Types.StringType(),
  badgeIconUrl: Schema.Types.StringType(),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  cityId: Schema.Types.NumberType(),
  countryId: Schema.Types.NumberType(),
  goldLevelAmount: Schema.Types.NumberType().isRequired('This field is required.'),
  silverLevelAmount: Schema.Types.NumberType().isRequired('This field is required.'),
  bronzeLevelAmount: Schema.Types.NumberType().isRequired('This field is required.'),
});


const emptyModel: UpsertStory = {
  id: undefined,
  name: '',
  description: '',
  bannerUrl: '',
  badgeIconUrl: '',
  status: StoryStatusEnum.Draft,
  cityId: undefined,
  countryId: undefined,
  goldLevelAmount: undefined,
  silverLevelAmount: undefined,
  bronzeLevelAmount: undefined,
};

interface StoryFormProps{
  formObject?: Story;
  pageState: PageState
};

const StoryForm: React.FC<StoryFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertStory>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      setFormValue(formObject);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<Story> = await upsertStory(formValue);
      if (result.success) {
        if(pageState == PageState.New){
          nav(`/stories/${result.result.id}`, {state:{tab: 'story-chapters'}});
        } else {
          nav(`/stories`);
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='story-form' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={storySchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}} style={{textAlign: 'center'}}>
              <Form.Group>
                <Form.Label>Banner</Form.Label>              
                <Form.Control
                  accepter={ImageUploader}
                  name="bannerUrl"
                  height="400px"
                  url={formValue.bannerUrl}
                  objectStorageCategory={ObjectStorageFileCategory.StoryBanner}
                  onChange={url => setFormValue(pre => ({...pre, bannerUrl: url}))}                
                />            
              </Form.Group>
              <Form.Group>
                <Form.Label>Badge Icon</Form.Label>              
                <Form.Control
                  accepter={ImageUploader}
                  name="logoUrl"
                  height="150px"
                  width="150px"
                  url={formValue.badgeIconUrl}
                  objectStorageCategory={ObjectStorageFileCategory.StoryBadge}
                  onChange={url => setFormValue(pre => ({...pre, badgeIconUrl: url}))}                
                />            
              </Form.Group>                
            </Col> 
            <Col span={{xs:24, xl:16}} >
              <Row>
                <Col span={{xs:24}} >
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control name="name"/>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={4} /> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>             
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>Country</Form.Label>
                    <Form.Control name="countryId" accepter={FetchingSelect} block
                      fetchData={(text, page) => getCountriesLookup(text, page)} 
                      placeholder="Select a country"
                      onChange={() => setFormValue(pre => ({...pre, cityId: undefined}))}
                    /> 
                  </Form.Group>
                </Col>                                  
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>City</Form.Label>
                    <Form.Control name="cityId" accepter={FetchingSelect} block
                      fetchData={(text, page) => getCitiesLookup(text, formValue?.countryId, page)} 
                      placeholder="Select a city"
                      reFetchOnOpen={true}
                    /> 
                  </Form.Group>
                </Col>                
              </Row>
              <Row>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Control name="status" accepter={SelectPicker} block
                      data={StoryStatusList} 
                      searchable={false}
                      placeholder="Select a status"
                      />                 
                  </Form.Group>
                </Col>
                <Col span={{xs: 24, lg:12}} >
                  <Form.Group >
                    <Form.Label>Is Charity</Form.Label>
                    <Form.Control name="isCharity" accepter={Checkbox}
                      checked={!!formValue.isCharity} 
                      onChange={(val, chk) => setFormValue(pre => ({ ...pre, isCharity: chk })) } 
                    />
                  </Form.Group>
                </Col>                 
              </Row>
              <Row>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Gold Level Amount</Form.Label>
                    <Form.Control name="goldLevelAmount" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Silver Level Amount</Form.Label>
                    <Form.Control name="silverLevelAmount" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:6}} >
                  <Form.Group>
                    <Form.Label>Bronze Level Amount</Form.Label>
                    <Form.Control name="bronzeLevelAmount" accepter={NumberInput}/>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default StoryForm;

